import { type useAnnouncementItemData } from '@components/announcement/hooks/useAnnouncementItemData.hooks';
import { type useAnnouncementRange } from '@components/announcement/hooks/useAnnouncementRange.hooks';
import {
  type AnnouncementItemEditorState,
  fromAnnouncementItemEditorState,
} from '@components/announcement/util/announcementItemEditorState.util';
import { toPlainText } from '@components/announcement/util/announcementRichTextContent.util';
import { useNavigate } from '@tanstack/react-router';
import { type JSONContent } from '@tiptap/core';
import { useSaveAnnouncementItemMutation } from '@/hooks';
import { type AnnouncementRichTextContentDTO } from '@/model';
import { APP_ROUTES } from '@/util';

interface UseSaveAnnouncementItemProps {
  announcementId: number;
  // undefined means we're saving a not-yet-saved item — see NEW_ANNOUNCEMENT_ITEM_ID.
  itemId: number | undefined;
  fetchedAnnouncement: ReturnType<typeof useAnnouncementItemData>['fetchedAnnouncement'];
  baseItem: ReturnType<typeof useAnnouncementRange>['baseItem'];
  editorState: AnnouncementItemEditorState;
  content: JSONContent;
  richTextContent: AnnouncementRichTextContentDTO;
  textToSpeech: string;
  onMissingRange: () => void;
  onMissingName: () => void;
  onMissingContent: () => void;
  onSaved: () => void;
  // See useAnnouncementRange's onItemSaved — keeps baseItem's id in sync
  // after a brand-new item's first save.
  onItemSaved: (id: number) => void;
}

interface UseSaveAnnouncementItemResult {
  onSave: () => Promise<boolean | void>;
  isSaving: boolean;
}

export const useSaveAnnouncementItem = ({
  announcementId,
  itemId,
  fetchedAnnouncement,
  baseItem,
  editorState,
  content,
  richTextContent,
  textToSpeech,
  onMissingRange,
  onMissingName,
  onMissingContent,
  onSaved,
  onItemSaved,
}: UseSaveAnnouncementItemProps): UseSaveAnnouncementItemResult => {
  const navigate = useNavigate();
  const saveAnnouncementItemMutation = useSaveAnnouncementItemMutation();

  const onSave = async () => {
    if (!fetchedAnnouncement) {
      return;
    }

    if (!baseItem) {
      //If range misses, we open the modal and cancel the save
      onMissingRange();
      return;
    }

    if (!editorState.name?.trim()) {
      onMissingName();
      return false;
    }

    const hasText = toPlainText(content).trim().length > 0;
    const hasImage = editorState.imageId != null || editorState.file != null;
    if (!hasText && !hasImage) {
      onMissingContent();
      return false;
    }

    const item = fromAnnouncementItemEditorState(
      editorState,
      { plainTextContent: toPlainText(content), richTextContent, textToSpeech },
      baseItem,
      announcementId,
    );

    const { isSuccess, data: savedItemId } =
      await saveAnnouncementItemMutation.mutateAsync(item);

    if (isSuccess) {
      onSaved();

      // We were creating a new item (URL still says .../new) — swap the URL
      // over to the id we were just saved under, so a further save (or a
      // reload) edits this item instead of creating another one. baseItem's
      // id needs the same update, otherwise the next save's payload still
      // goes out without one and the backend creates yet another item.
      if (itemId == null) {
        onItemSaved(savedItemId);

        await navigate({
          to: APP_ROUTES.ANNOUNCEMENTS.EDITOR,
          params: { id: announcementId, itemId: savedItemId },
          replace: true,
        });
      }
    }

    return isSuccess;
  };

  return {
    onSave,
    isSaving: saveAnnouncementItemMutation.isPending,
  };
};
